import { useEffect, useRef, useState, useCallback } from 'react';
import { showSessionExpiredModal } from './utilities/session_expired_modal';
import cookieStorage from './utilities/cookie_storage';

const InactivityTracker = ({ children }) => {
  const [isAuthenticated] = useState(!!cookieStorage.getItem('token'));
  const timeoutRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);

  const getInactivityLimit = () => {
    try {
      const raw = cookieStorage.getItem('sessionTimeout');
      const settings = raw ? JSON.parse(raw) : {};
      let { days = 0, hours = 0, minutes = 0, enabled = false } = settings;
      if (!enabled) return null;

      if (days >= 7) {
        days = 6;
        hours = 23;
        minutes = 59;
      }
      return (
        days * 24 * 60 * 60 * 1000 +
        hours * 60 * 60 * 1000 +
        minutes * 60 * 1000
      );
    } catch {
      return null;
    }
  };

  const clearTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleContinue = useCallback(() => {
    setModalOpen(false);
    sessionStorage.setItem('lastActivity', Date.now().toString());
  }, []);

  const startInactivityTimer = useCallback(() => {
    if (!isAuthenticated || modalOpen) return;

    clearTimer();

    const checkInactivity = () => {
      const inactivityLimit = getInactivityLimit();
      if (!inactivityLimit && inactivityLimit !== 0) return;

      const lastActivity = Number(sessionStorage.getItem('lastActivity')) || Date.now();
      const elapsed = Date.now() - lastActivity;
      const remaining = Math.max(inactivityLimit - elapsed, 0);

      const warnThreshold = 5 * 60 * 1000;

      if (remaining <= warnThreshold) {
        showSessionExpiredModal(handleContinue, remaining);
        setModalOpen(true);
      } else {
        const timeUntilWarn = remaining - warnThreshold;
        timeoutRef.current = setTimeout(checkInactivity, timeUntilWarn);
      }
    };

    checkInactivity();
  }, [isAuthenticated, modalOpen, handleContinue]);

  useEffect(() => {
    const events = ['mousemove', 'click', 'keydown', 'scroll', 'touchstart'];
    let lastWrite = 0;

    const markActivity = () => {
      if (!isAuthenticated || modalOpen) return;
      const now = Date.now();
      // Throttle sessionStorage updates to max 1 per second
      if (now - lastWrite > 1000) {
        sessionStorage.setItem('lastActivity', now.toString());
        lastWrite = now;
      }
    };

    if (isAuthenticated && !modalOpen) {
      startInactivityTimer();
    }

    events.forEach(event => window.addEventListener(event, markActivity, { passive: true }));

    return () => {
      events.forEach(event => window.removeEventListener(event, markActivity));
      clearTimer();
    };
  }, [isAuthenticated, modalOpen, startInactivityTimer]);

  return <>{children}</>;
};

export default InactivityTracker;
